import { Copy, Download, Eye, MoreVertical, Trash2 } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";

interface FileActionsMenuProps {
  onPreview: () => void;
  onDownload: () => void;
  onDelete: () => void;
  onCopyUrl: () => void;
  isPreviewable: boolean;
}

const FileActionsMenu: React.FC<FileActionsMenuProps> = ({
  onPreview,
  onDownload,
  onDelete,
  onCopyUrl,
  isPreviewable,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const menuItems = [
    ...(isPreviewable
      ? [
          {
            icon: Eye,
            label: "Preview",
            onClick: onPreview,
            color: "text-blue-500",
          },
        ]
      : []),
    {
      icon: Download,
      label: "Download",
      onClick: onDownload,
      color: isDarkMode ? "text-rp-grey/70" : "text-gray-700",
    },
    {
      icon: Copy,
      label: "Copy URL",
      onClick: onCopyUrl,
      color: isDarkMode ? "text-rp-grey/70" : "text-gray-700",
    },
    {
      icon: Trash2,
      label: "Delete",
      onClick: onDelete,
      color: "text-red-500",
    },
  ];

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-1.5 rounded transition-colors ${
          isDarkMode
            ? "hover:bg-rp-surface-raised text-rp-grey/50 hover:text-rp-grey"
            : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
        }`}
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 z-50 mt-1 w-40 rounded-lg shadow-lg border ${
            isDarkMode
              ? "bg-rp-surface border-rp-separator"
              : "bg-white border-gray-200"
          }`}
        >
          {menuItems.map((item, index) => (
            <button
              key={index}
              onClick={() => {
                item.onClick();
                setIsOpen(false);
              }}
              className={`w-full flex items-center space-x-2 px-3 py-2 text-sm transition-colors ${
                isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-50"
              } ${index === 0 ? "rounded-t-lg" : ""} ${index === menuItems.length - 1 ? "rounded-b-lg" : ""}`}
            >
              <item.icon className={`w-4 h-4 ${item.color}`} />
              <span className={item.color}>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileActionsMenu;

